import { settingsService } from './settingsService';

/**
 * Service to handle favicon operations
 */
export const faviconService = {
  /**
   * Updates the site favicon and related icons based on the current logo
   * @returns {Promise<boolean>} Success status
   */
  async updateFaviconFromLogo(): Promise<boolean> {
    try {
      // Get current settings to get logo URL
      const settings = await settingsService.getSettings();
      const logoUrl = settings.general.logoUrl;
      
      if (!logoUrl) {
        console.warn('No logo URL found in settings');
        return false;
      }

      // Try to fetch the image first to handle CORS properly
      const response = await fetch(logoUrl, {
        mode: 'cors',
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch logo: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      // Create a new image with CORS settings
      const img = new Image();
      
      // Create a promise to handle image loading
      const imageLoadPromise = new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            // Create a canvas to draw the image
            const canvas = document.createElement('canvas');
            canvas.width = 32; // favicon size
            canvas.height = 32;
            
            // Draw the image
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }
            
            // Draw image maintaining aspect ratio
            const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
            const x = (canvas.width - img.width * scale) / 2;
            const y = (canvas.height - img.height * scale) / 2;
            ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
            
            // Convert to favicon
            const faviconUrl = canvas.toDataURL('image/png');
            
            // Update favicon in the document
            let favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
            if (!favicon) {
              favicon = document.createElement('link');
              favicon.rel = 'icon';
              document.head.appendChild(favicon);
            }
            favicon.href = faviconUrl;
            
            // Clean up
            URL.revokeObjectURL(objectUrl);
            resolve(true);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Failed to load logo image'));
      });

      // Set the image source after setting up handlers
      img.src = objectUrl;
      
      // Wait for the image to load and process
      await imageLoadPromise;
      return true;
    } catch (error) {
      console.error('Error updating favicon:', error);
      // Provide more detailed error information
      if (error instanceof Error) {
        if (error.message.includes('CORS')) {
          console.error('CORS error detected. Please ensure CORS is properly configured in Firebase Storage.');
        }
      }
      return false;
    }
  }
}; 